import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowRight, Camera, Code2, Crown, Rocket, Sparkles, Star, Tag, Users } from "lucide-react";

const PACKS = [
  { sessions: 30, discount_pct: 0, label: "Starter", accent: "#FFE66D" },
  { sessions: 60, discount_pct: 10, label: "Popular", accent: "#4ECDC4" },
  { sessions: 90, discount_pct: 15, label: "Best value", accent: "#FF6B6B" },
];

export default function KidsHome() {
  const [offerings, setOfferings] = useState([]);
  const { user } = useAuth();
  const { currency, format } = useCurrency();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/catalog/kids-offerings").then((r) => setOfferings(r.data)).catch(() => {});
  }, []);

  const subjectIcon = (s) => s === "Coding" ? <Code2 className="w-6 h-6" /> : s === "Photography" ? <Camera className="w-6 h-6" /> : <Rocket className="w-6 h-6" />;

  return (
    <div className="min-h-screen bg-[#FDF8F5] text-[#2B2D42]">
      <Navbar variant="kids" />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 grid md:grid-cols-2 gap-10 items-center relative">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-white kids-shadow px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-[#FF6B6B]">
              <Sparkles className="w-3.5 h-3.5" /> For Curious Kids
            </div>
            <h1 className="mt-6 font-kids-heading text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight">
              Build. Click. <span className="text-[#FF6B6B]">Launch.</span>
            </h1>
            <p className="mt-5 text-lg text-[#556074] max-w-md leading-relaxed">
              Small-group (6–8 kids) sessions in Coding, Photography, and Aerospace — ages 5 to 16. Buy sessions in packs and use them at your family&apos;s pace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="kids-cta-browse" onClick={() => document.getElementById("kids-programs")?.scrollIntoView({ behavior: "smooth" })} className="rounded-full bg-[#FF6B6B] hover:bg-[#e85a5a] text-white kids-shadow px-6 py-6 text-base">
                Browse programs <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button asChild variant="ghost" className="rounded-full text-[#2B2D42] hover:bg-white px-6 py-6 text-base">
                <Link to="/signup">Parents, sign up →</Link>
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-[#FFE66D] kids-blob" />
            <div className="absolute -bottom-8 -right-4 w-48 h-48 rounded-full bg-[#4ECDC4]/50 kids-blob" />
            <img src="https://images.unsplash.com/photo-1623076189461-f7706b741c04?w=1000" alt="" className="relative rounded-[2.5rem] kids-shadow-teal w-full max-h-[520px] object-cover" />
          </div>
        </div>
      </section>

      {/* Programs */}
      <section id="kids-programs" className="max-w-7xl mx-auto px-6 py-14">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#8D99AE] font-bold mb-3">Programs</div>
          <h2 className="font-kids-heading text-4xl md:text-5xl font-bold leading-tight">
            Three subjects. <span className="text-[#4ECDC4]">Four pack sizes.</span>
          </h2>
          {offerings[0] && (
            <p data-testid="kids-pricing-summary" className="mt-4 text-[#556074] max-w-lg">Sessions are {format(offerings[0][`per_session_price_${currency}`])} each. Every program comes in 30, 60, or 90-session packs (with growing discounts), or grab the Grand pack of 100 sessions for a flat {format(offerings[0][`flat_100_price_${currency}`])}.</p>
          )}
        </div>
        <div className="space-y-10">
          {offerings.map((o) => (
            <OfferingRow
              key={o.offering_id}
              offering={o}
              icon={subjectIcon(o.subject)}
              user={user}
              onLoginRequired={() => { toast.info("Please sign in to book"); nav("/login?next=/kids"); }}
              currency={currency}
              format={format}
            />
          ))}
        </div>
      </section>

      {/* Parent trust */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="rounded-[2rem] bg-white kids-shadow p-10 md:p-14 grid md:grid-cols-3 gap-10">
          {[
            { t: "Safe & small groups", d: "Max 6–8 kids per group so every voice is heard." },
            { t: "Parent updates", d: "Session summaries shared with parents after each class." },
            { t: "Real projects", d: "Kids finish every pack with something to show off." },
          ].map((x) => (
            <div key={x.t}>
              <div className="flex items-center gap-2 text-[#FF6B6B] mb-2"><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /></div>
              <h3 className="font-kids-heading text-xl font-bold">{x.t}</h3>
              <p className="mt-2 text-sm text-[#556074] leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer variant="kids" />
    </div>
  );
}

function OfferingRow({ offering, icon, user, onLoginRequired, currency, format }) {
  const [coupon, setCoupon] = useState("");
  const [couponMeta, setCouponMeta] = useState(null);
  const [busy, setBusy] = useState(null);

  const perSession = offering[`per_session_price_${currency}`];
  const flat100 = offering[`flat_100_price_${currency}`];

  const priceFor = (sessions) => {
    if (sessions === 100) return flat100;
    const pack = PACKS.find((p) => p.sessions === sessions);
    const raw = perSession * sessions * (1 - pack.discount_pct / 100);
    return currency === "inr" ? Math.round(raw) : raw;
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal: priceFor(30), currency });
      setCouponMeta(data); toast.success(`Coupon applied · saved ${format(data.discount)} on 30-pack (and more on larger)`);
    } catch (e) { setCouponMeta(null); toast.error(e.response?.data?.detail || "Invalid coupon"); }
  };

  const book = async (sessions) => {
    if (!user) return onLoginRequired();
    setBusy(sessions);
    try {
      const { data } = await api.post("/payments/checkout", {
        item_kind: "kids_pack", item_id: offering.offering_id, origin_url: window.location.origin,
        currency, kids_sessions: sessions,
        coupon_code: couponMeta ? coupon : undefined,
      });
      if (data.provider === "razorpay") {
        const { openRazorpayCheckout } = await import("@/lib/razorpay");
        await openRazorpayCheckout({
          orderData: data,
          onSuccess: async (resp) => {
            try {
              await api.post("/payments/razorpay/verify", {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              window.location.href = `/payment/return?session_id=${data.session_id}`;
            } catch (err) { toast.error("Payment verification failed."); }
          },
          onDismiss: () => setBusy(null),
          onError: () => toast.error("Payment failed. Please try again."),
        });
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid={`kids-offering-${offering.offering_id}`} className="bg-white rounded-[2rem] kids-shadow overflow-hidden">
      <div className="grid md:grid-cols-12 gap-0">
        <div className="md:col-span-4 relative overflow-hidden bg-[#F3E8E0] min-h-[280px]">
          <img src={offering.thumbnail} alt="" className="w-full h-full object-cover"/>
          <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
            {offering.age_tiers?.map((t) => (
              <span key={t} className="text-[10px] font-bold uppercase tracking-[0.1em] bg-white rounded-full px-2.5 py-1 text-[#FF6B6B]">Ages {t}</span>
            ))}
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-3 bg-white rounded-2xl p-3 kids-shadow">
              <div className="w-11 h-11 rounded-2xl bg-[#FFE66D] flex items-center justify-center text-[#2B2D42]">{icon}</div>
              <div className="min-w-0">
                <div className="font-kids-heading text-lg font-bold truncate">{offering.title}</div>
                <div className="text-xs text-[#8D99AE] truncate">{offering.tagline}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-8 p-6 md:p-8">
          <p className="text-sm text-[#556074] leading-relaxed max-w-2xl">{offering.description}</p>
          <div className="mt-2 text-xs text-[#8D99AE] font-bold uppercase tracking-[0.15em] flex items-center gap-2"><Users className="w-3.5 h-3.5"/>{offering.group_size} · {format(perSession)}/session</div>

          {/* Trial CTA */}
          <div className="mt-5 rounded-2xl bg-gradient-to-r from-[#4ECDC4]/15 to-[#FFE66D]/15 border border-[#4ECDC4]/40 p-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4ECDC4]">Try before committing</div>
              <div className="font-kids-heading text-lg font-bold text-[#2B2D42] mt-0.5">1 sample session · {format(perSession)}</div>
              <div className="text-[11px] text-[#8D99AE] mt-0.5">See if the class clicks with your child, no pack required</div>
            </div>
            <Button
              onClick={() => book(1)}
              disabled={busy === 1}
              data-testid={`kids-trial-${offering.offering_id}`}
              className="rounded-full bg-[#4ECDC4] hover:bg-[#3fb8b0] text-white font-bold px-5 py-5 shrink-0"
            >
              {busy === 1 ? "…" : "Book trial"}
            </Button>
          </div>

          {/* 4-pack grid */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PACKS.map((p) => {
              const totalBase = perSession * p.sessions;
              const totalAfter = priceFor(p.sessions);
              return (
                <PackCard
                  key={p.sessions}
                  data-testid={`kids-pack-${offering.offering_id}-${p.sessions}`}
                  label={p.label}
                  sessions={p.sessions}
                  price={totalAfter}
                  strike={p.discount_pct > 0 ? totalBase : null}
                  badge={p.discount_pct > 0 ? `-${p.discount_pct}%` : "no discount"}
                  accent={p.accent}
                  busy={busy === p.sessions}
                  onClick={() => book(p.sessions)}
                  testid={`kids-book-${offering.offering_id}-${p.sessions}`}
                  format={format}
                />
              );
            })}
            <PackCard
              label="Grand pack"
              sessions={100}
              price={flat100}
              strike={perSession * 100}
              badge={`Save ${format(perSession * 100 - flat100)}`}
              accent="#8B5CF6"
              tier="grand"
              busy={busy === 100}
              onClick={() => book(100)}
              testid={`kids-book-${offering.offering_id}-100`}
              format={format}
            />
          </div>

          {/* Coupon */}
          <div className="mt-6 flex items-center gap-2 max-w-md">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8D99AE] font-bold flex items-center gap-1.5"><Tag className="w-3 h-3"/>Coupon</div>
            <Input data-testid={`kids-coupon-${offering.offering_id}`} placeholder="LAUNCH10" value={coupon} onChange={(e)=>setCoupon(e.target.value.toUpperCase())} className="h-9 bg-[#FDF8F5] border-[#F3E8E0] rounded-xl text-sm max-w-[180px] font-adult-mono"/>
            <Button variant="outline" onClick={applyCoupon} className="h-9 rounded-xl border-[#F3E8E0] text-[#2B2D42]">Apply</Button>
            {couponMeta && <span className="text-[11px] text-[#4ECDC4] font-bold">✓ applied</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PackCard({ label, sessions, price, strike, badge, accent, tier, busy, onClick, testid, format }) {
  const isGrand = tier === "grand";
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`text-left rounded-2xl p-4 transition-smooth border-2 hover:-translate-y-0.5 relative overflow-hidden ${isGrand ? "bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] text-white border-transparent kids-shadow-teal" : "bg-[#FDF8F5] hover:bg-white border-[#F3E8E0]"}`}
    >
      {isGrand && <Crown className="absolute top-3 right-3 w-4 h-4 text-white/80"/>}
      <div className="flex items-center justify-between">
        <div className={`text-[10px] font-bold uppercase tracking-[0.15em] ${isGrand ? "text-white/80" : "text-[#8D99AE]"}`}>{label}</div>
        <span
          className="text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-full"
          style={{ background: isGrand ? "rgba(255,255,255,0.2)" : accent + "33", color: isGrand ? "#fff" : "#2B2D42" }}
        >
          {badge}
        </span>
      </div>
      <div className={`mt-2 font-kids-heading text-3xl font-bold ${isGrand ? "text-white" : "text-[#FF6B6B]"}`}>
        {sessions}
        <span className={`text-sm font-normal ml-1 ${isGrand ? "text-white/70" : "text-[#8D99AE]"}`}>sessions</span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className={`font-kids-heading text-lg font-bold ${isGrand ? "text-white" : "text-[#2B2D42]"}`}>{format(price)}</div>
        {strike && <div className={`text-xs line-through ${isGrand ? "text-white/60" : "text-[#8D99AE]"}`}>{format(strike)}</div>}
      </div>
      <div className={`mt-3 text-xs font-bold ${isGrand ? "text-white/90" : "text-[#4ECDC4]"}`}>
        {busy ? "…" : "Book this pack →"}
      </div>
    </button>
  );
}
